import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, MapPin, Users, Calendar, Trophy, Shield, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { getToken } from '../lib/auth';
import { badgeUrl, playerUrl, wcPlayerUrl } from '../lib/assetUrls';
import { isWcTeam } from '../lib/wcTeams';
import { PlayerProfileModal } from '../components/PlayerProfileModal';
import { useKioskInactivity } from '../hooks/useKioskInactivity';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function authHeaders() { return { Authorization: `Bearer ${getToken()}` }; }

// ── Shared atoms ──────────────────────────────────────────────────────────────

function TeamBadge({ teamId, teamName, size = 'xl' }) {
    const [err, setErr] = useState(false);
    const sizes = { sm: 'w-6 h-6', md: 'w-10 h-10', lg: 'w-16 h-16', xl: 'w-20 h-20' };
    const texts = { sm: 'text-xs', md: 'text-sm', lg: 'text-2xl', xl: 'text-3xl' };
    const dim = sizes[size] || sizes.xl;
    const txt = texts[size] || texts.xl;
    if (err || !teamId) {
        return (
            <div className={`${dim} rounded-full bg-white/10 flex items-center justify-center flex-shrink-0`}>
                <span className={`${txt} font-bold text-white/60`}>{teamName?.charAt(0) || '?'}</span>
            </div>
        );
    }
    return (
        <img src={badgeUrl(teamId)} alt={teamName}
            className={`${dim} object-contain flex-shrink-0`} onError={() => setErr(true)} />
    );
}

function SmallTeamBadge({ teamId, teamName }) {
    const [err, setErr] = useState(false);
    if (err || !teamId) {
        return (
            <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-muted-foreground">{teamName?.charAt(0) || '?'}</span>
            </div>
        );
    }
    return (
        <img src={badgeUrl(teamId)} alt={teamName}
            className="w-5 h-5 object-contain flex-shrink-0" onError={() => setErr(true)} />
    );
}

function PlayerCircle({ playerId, playerName, teamId }) {
    const [err, setErr] = useState(false);
    if (!err && playerId) {
        const src = isWcTeam(teamId) ? wcPlayerUrl(playerId) : playerUrl(playerId);
        return (
            <img src={src} alt={playerName}
                className="w-10 h-10 rounded-full object-cover object-top flex-shrink-0"
                onError={() => setErr(true)} />
        );
    }
    return (
        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-muted-foreground leading-none">
                {playerName?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
            </span>
        </div>
    );
}

// ── Team Header ───────────────────────────────────────────────────────────────

function TeamHeader({ overview, loading, teamId }) {
    return (
        <div className="rounded-2xl overflow-hidden border border-white/5 shadow-xl">
            <div className="bg-gradient-to-br from-[#0a0a1a] via-[#000028] to-[#0d0d1d] px-6 py-6">
                <div className="flex items-center gap-5">
                    {loading ? (
                        <Skeleton className="w-20 h-20 rounded-full bg-white/10" />
                    ) : (
                        <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center p-1">
                            <TeamBadge teamId={teamId} teamName={overview?.team_name} size="xl" />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        {loading ? (
                            <>
                                <Skeleton className="h-7 w-48 bg-white/10 mb-2" />
                                <Skeleton className="h-4 w-64 bg-white/10" />
                            </>
                        ) : (
                            <>
                                <h1 className="text-2xl font-bold text-white leading-tight truncate page-title">
                                    {overview?.team_name || 'Team'}
                                </h1>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                                    {overview?.manager_name && (
                                        <span className="text-sm text-white/60 flex items-center gap-1">
                                            <Users className="h-3.5 w-3.5" />
                                            {overview.manager_name}
                                        </span>
                                    )}
                                    {overview?.ground_name && (
                                        <span className="text-sm text-white/60 flex items-center gap-1">
                                            <MapPin className="h-3.5 w-3.5" />
                                            {overview.ground_name}{overview.ground_city ? `, ${overview.ground_city}` : ''}
                                        </span>
                                    )}
                                    {overview?.founded && (
                                        <span className="text-sm text-white/60 flex items-center gap-1">
                                            <Calendar className="h-3.5 w-3.5" />
                                            Est. {overview.founded}
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────

function ColourPill({ colour, label }) {
    if (!colour) return null;
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border border-white/10"
            style={{ backgroundColor: colour.toLowerCase().startsWith('#') ? colour : undefined }}
        >
            <span
                className="w-3 h-3 rounded-full border border-white/20 flex-shrink-0"
                style={{ backgroundColor: colour }}
            />
            <span className="text-foreground/80">{label}: {colour}</span>
        </span>
    );
}

function FormPill({ result }) {
    const colours = {
        W: 'bg-green-500/15 text-green-400 border-green-500/30',
        D: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
        L: 'bg-red-500/15 text-red-400 border-red-500/30',
    };
    return (
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold border ${colours[result] || ''}`}>
            {result}
        </span>
    );
}

function OverviewTab({ overview }) {
    if (!overview) return null;

    const cups = overview.cup_competitions || [];
    const form = overview.recent_form || [];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Club Info card */}
            <div className="rounded-2xl border bg-card p-5 space-y-3">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    Club Info
                </h3>
                <div className="space-y-2 text-sm">
                    {overview.ground_name && (
                        <div className="flex justify-between gap-2">
                            <span className="text-muted-foreground flex items-center gap-1.5">
                                <MapPin className="h-3.5 w-3.5" /> Ground
                            </span>
                            <span className="font-medium text-right">
                                {overview.ground_name}{overview.ground_city ? `, ${overview.ground_city}` : ''}
                            </span>
                        </div>
                    )}
                    {overview.ground_capacity && (
                        <div className="flex justify-between gap-2">
                            <span className="text-muted-foreground">Capacity</span>
                            <span className="font-medium">{parseInt(overview.ground_capacity, 10).toLocaleString()}</span>
                        </div>
                    )}
                    {overview.manager_name && (
                        <div className="flex justify-between gap-2">
                            <span className="text-muted-foreground">Manager</span>
                            <span className="font-medium">{overview.manager_name}</span>
                        </div>
                    )}
                    {overview.manager_nationality && (
                        <div className="flex justify-between gap-2">
                            <span className="text-muted-foreground">Nationality</span>
                            <span className="font-medium">{overview.manager_nationality}</span>
                        </div>
                    )}
                    {overview.founded && (
                        <div className="flex justify-between gap-2">
                            <span className="text-muted-foreground">Founded</span>
                            <span className="font-medium">{overview.founded}</span>
                        </div>
                    )}
                    {/* Recent form */}
                    {form.length > 0 && (
                        <div className="pt-1 border-t border-border/50">
                            <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide block mb-2">Recent Form</span>
                            <div className="flex items-center gap-1.5">
                                {form.map((f, i) => (
                                    <div key={i} className="group relative">
                                        <FormPill result={f.result} />
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block z-10 pointer-events-none">
                                            <div className="bg-popover border border-border rounded-lg px-2.5 py-1.5 text-xs whitespace-nowrap shadow-lg">
                                                <span className="font-semibold">{f.score}</span>
                                                <span className="text-muted-foreground"> vs {f.opponent}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Cup Competitions card */}
            <div className="rounded-2xl border bg-card p-5 space-y-3">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                    <Trophy className="h-4 w-4 text-muted-foreground" />
                    Cup Competitions
                </h3>
                {cups.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No cup data for this season.</p>
                ) : (
                    <div className="space-y-3">
                        {cups.map((c, i) => (
                            <div key={i} className="flex items-start justify-between gap-2 text-sm">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5">
                                        {c.is_trophy && <span className="text-base leading-none">🏆</span>}
                                        <p className="font-medium truncate">{c.competition}</p>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5">{c.last_round}</p>
                                    {c.opponent && (
                                        <p className="text-xs text-muted-foreground">
                                            vs {c.opponent} — {c.score}
                                            {c.aggregate_score && (
                                                <span className="ml-1 text-muted-foreground/70">(agg {c.aggregate_score})</span>
                                            )}
                                        </p>
                                    )}
                                </div>
                                <Badge
                                    variant="outline"
                                    className={`flex-shrink-0 text-xs ${
                                        c.outcome === 'Won' ? 'border-green-500/50 text-green-400 bg-green-500/10' :
                                        c.outcome === 'Eliminated' ? 'border-red-500/50 text-red-400 bg-red-500/10' :
                                        'border-yellow-500/50 text-yellow-400 bg-yellow-500/10'
                                    }`}
                                >
                                    {c.is_trophy ? 'Champions' : c.outcome}
                                </Badge>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Fixtures & Results Tab ────────────────────────────────────────────────────

function fmtKickoff(iso) {
    if (!iso) return '';
    try { return format(parseISO(iso), 'EEE d MMM, HH:mm'); } catch { return iso; }
}

function fmtDate(iso) {
    if (!iso) return '';
    try { return format(parseISO(iso), 'EEE d MMM'); } catch { return iso; }
}

function MatchRow({ match, teamId, navigate, isResult }) {
    const isHome = String(match.home_team_id) === String(teamId);
    const isAway = String(match.away_team_id) === String(teamId);
    const isLive = match.status === 'live';
    const hasScore = match.home_score != null && match.away_score != null;

    // W/D/L pill for results
    let resultBg = '';
    if (isResult && hasScore) {
        const ourScore = isHome ? match.home_score : match.away_score;
        const oppScore = isHome ? match.away_score : match.home_score;
        if (ourScore > oppScore) resultBg = 'bg-green-500/15 text-green-400';
        else if (ourScore < oppScore) resultBg = 'bg-red-500/15 text-red-400';
        else resultBg = 'bg-yellow-500/15 text-yellow-400';
    }

    const handleClick = () => {
        if (!match.pa_match_id) return;
        navigate(`/football-hub/match/${match.pa_match_id}`, {
            state: {
                match: {
                    pa_match_id:    match.pa_match_id,
                    competition:    match.competition,
                    round:          match.round,
                    kickoff_time:   match.kickoff_time,
                    home_team_id:   match.home_team_id,
                    home_team_name: match.home_team_name,
                    away_team_id:   match.away_team_id,
                    away_team_name: match.away_team_name,
                    home_score:     match.home_score ?? null,
                    away_score:     match.away_score ?? null,
                    attendance:     match.attendance ?? null,
                    venue:          match.venue || '',
                    status:         match.status || (isResult ? 'finished' : 'fixture'),
                }
            }
        });
    };

    return (
        <button
            onClick={handleClick}
            className={`w-full rounded-xl border p-3 hover:border-primary/40 hover:bg-muted/30 transition-colors text-left ${isLive ? 'border-green-500/40 bg-green-500/5' : 'bg-card'}`}
        >
            <div className="flex items-center gap-2">
                {/* Date */}
                <div className="w-20 flex-shrink-0">
                    <p className="text-xs text-muted-foreground leading-tight">{fmtDate(match.kickoff_time)}</p>
                    <p className="text-xs text-muted-foreground/70">{match.kickoff_time ? format(parseISO(match.kickoff_time), 'HH:mm') : ''}</p>
                </div>

                {/* Teams + score */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-center gap-2">
                        <div className={`flex items-center gap-1.5 flex-1 justify-end min-w-0 ${isHome ? 'font-semibold' : ''}`}>
                            <span className="text-sm truncate text-right">{match.home_team_name}</span>
                            <SmallTeamBadge teamId={match.home_team_id} teamName={match.home_team_name} />
                        </div>
                        {hasScore ? (
                            <span className={`text-sm font-bold tabular-nums flex-shrink-0 px-1.5 py-0.5 rounded ${isLive ? 'bg-green-500/20 text-green-400' : resultBg}`}>
                                {match.home_score} – {match.away_score}
                                {isLive && <span className="ml-1 text-[10px] font-semibold text-green-400 animate-pulse">{match.phase_label || 'LIVE'}</span>}
                            </span>
                        ) : (
                            <span className="text-xs text-muted-foreground font-medium flex-shrink-0">vs</span>
                        )}
                        <div className={`flex items-center gap-1.5 flex-1 justify-start min-w-0 ${isAway ? 'font-semibold' : ''}`}>
                            <SmallTeamBadge teamId={match.away_team_id} teamName={match.away_team_name} />
                            <span className="text-sm truncate">{match.away_team_name}</span>
                        </div>
                    </div>
                    {(match.venue || match.competition) && (
                        <p className="text-xs text-muted-foreground/60 text-center mt-0.5 truncate">
                            {match.venue || match.competition}
                        </p>
                    )}
                </div>

            </div>
        </button>
    );
}

function FixturesResultsTab({ fixtures, fixturesLoading, results, resultsLoading, liveMatch, teamId, navigate }) {
    const TWO_MONTHS_AGO = new Date();
    TWO_MONTHS_AGO.setMonth(TWO_MONTHS_AGO.getMonth() - 2);
    const cutoff = TWO_MONTHS_AGO.toISOString().slice(0, 10); // YYYY-MM-DD

    const [showAllResults, setShowAllResults] = useState(false);

    const recentResults = (results || []).filter(r => (r.sortable_date || '') >= cutoff);
    const olderResults  = (results || []).filter(r => (r.sortable_date || '') < cutoff);
    const displayedResults = showAllResults ? (results || []) : recentResults;

    const isLoading = fixturesLoading || resultsLoading;

    if (isLoading && !fixtures && !results) {
        return (
            <div className="space-y-2">
                {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Live match — pinned to top when in progress */}
            {liveMatch && (
                <div>
                    <div className="flex items-center gap-2 mb-3">
                        <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse inline-block" />
                        <h3 className="text-sm font-semibold text-green-500 uppercase tracking-wide">Live Now</h3>
                    </div>
                    <MatchRow match={liveMatch} teamId={teamId} navigate={navigate} isResult={false} />
                </div>
            )}

            {/* Results section */}
            <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Results</h3>
                {resultsLoading ? (
                    <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}</div>
                ) : !results || results.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-3">No results found.</p>
                ) : (
                    <div className="space-y-2">
                        {displayedResults.map((r, i) => (
                            <MatchRow key={r.pa_match_id || i} match={r} teamId={teamId} navigate={navigate} isResult />
                        ))}
                        {!showAllResults && olderResults.length > 0 && (
                            <button
                                onClick={() => setShowAllResults(true)}
                                className="w-full py-2.5 text-sm text-muted-foreground hover:text-foreground border border-dashed border-border rounded-xl transition-colors"
                            >
                                Show {olderResults.length} more result{olderResults.length !== 1 ? 's' : ''}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Fixtures section */}
            <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Upcoming Fixtures</h3>
                {fixturesLoading ? (
                    <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}</div>
                ) : !fixtures || fixtures.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-3">No upcoming fixtures.</p>
                ) : (
                    <div className="space-y-2">
                        {fixtures.map((fx, i) => (
                            <MatchRow key={fx.pa_match_id || i} match={fx} teamId={teamId} navigate={navigate} isResult={false} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Squad Tab ─────────────────────────────────────────────────────────────────

function PlayerRow({ player, onPlayerClick, teamId }) {
    return (
        <button
            className="w-full flex items-center gap-3 py-2 px-1 rounded-lg hover:bg-muted/40 transition-colors text-left"
            onClick={() => onPlayerClick({ player_id: player.player_id, player: player.name })}
        >
            <PlayerCircle playerId={player.player_id} playerName={player.name} teamId={teamId} />
            <span className="w-6 text-xs text-muted-foreground font-mono flex-shrink-0 text-right">
                {player.shirt || ''}
            </span>
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{player.name}</p>
                <p className="text-xs text-muted-foreground">{player.nationality}</p>
            </div>
            {player.age && (
                <span className="text-xs text-muted-foreground flex-shrink-0">{player.age}</span>
            )}
        </button>
    );
}

function PositionSection({ title, players, onPlayerClick, teamId }) {
    if (!players || players.length === 0) return null;
    return (
        <div className="rounded-2xl border bg-card p-4 space-y-1">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                {title}
                <span className="text-xs font-normal text-muted-foreground">({players.length})</span>
            </h3>
            <div className="divide-y divide-border/40">
                {players.map((p, i) => (
                    <PlayerRow key={p.player_id || i} player={p} onPlayerClick={onPlayerClick} teamId={teamId} />
                ))}
            </div>
        </div>
    );
}

function SquadTab({ squad, loading, onPlayerClick, teamId }) {
    if (loading) {
        return (
            <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-32 w-full rounded-2xl" />
                ))}
            </div>
        );
    }
    if (!squad || Object.keys(squad).length === 0) {
        return <p className="text-sm text-muted-foreground py-4 text-center">Squad not available.</p>;
    }
    return (
        <div className="space-y-3">
            <PositionSection title="Goalkeepers" players={squad.goalkeepers} onPlayerClick={onPlayerClick} teamId={teamId} />
            <PositionSection title="Defenders"   players={squad.defenders}   onPlayerClick={onPlayerClick} teamId={teamId} />
            <PositionSection title="Midfielders" players={squad.midfielders} onPlayerClick={onPlayerClick} teamId={teamId} />
            <PositionSection title="Forwards"    players={squad.forwards}    onPlayerClick={onPlayerClick} teamId={teamId} />
        </div>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function TeamPage() {
    const { teamId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isKioskRoute = location.pathname.startsWith('/wc2026/');

    // Redirect to /wc2026 after 5 min inactivity when reached via kiosk
    useKioskInactivity({ enabled: isKioskRoute });

    const [tab, setTab] = useState('overview');
    const [overview, setOverview] = useState(null);
    const [overviewLoading, setOverviewLoading] = useState(true);
    const [fixtures, setFixtures] = useState(null);
    const [fixturesLoading, setFixturesLoading] = useState(false);
    const [results, setResults] = useState(null);
    const [resultsLoading, setResultsLoading] = useState(false);
    const [liveMatch, setLiveMatch] = useState(null); // currently live match for this team
    const [squad, setSquad] = useState(null);
    const [squadLoading, setSquadLoading] = useState(false);
    const [profilePlayer, setProfilePlayer] = useState(null);
    const [following, setFollowing] = useState(false);
    const [followLoading, setFollowLoading] = useState(false);

    // Check if already following on mount
    useEffect(() => {
        axios.get(`${API_URL}/api/football/following/teams`, { headers: authHeaders() })
            .then(r => {
                const teams = r.data.teams || [];
                setFollowing(teams.some(t => String(t.team_id) === String(teamId)));
            })
            .catch(() => {});
    }, [teamId]);

    const handleFollowToggle = async () => {
        setFollowLoading(true);
        try {
            if (following) {
                await axios.delete(`${API_URL}/api/football/following/teams/${teamId}`, { headers: authHeaders() });
                setFollowing(false);
            } else {
                await axios.post(`${API_URL}/api/football/following/teams`,
                    { team_id: teamId, team_name: overview?.team_name || '' },
                    { headers: authHeaders() }
                );
                setFollowing(true);
            }
        } catch { /* silent */ }
        finally { setFollowLoading(false); }
    };

    // Fetch overview on mount
    useEffect(() => {
        setOverviewLoading(true);
        axios.get(`${API_URL}/api/football/hub/team/${teamId}`, { headers: authHeaders() })
            .then(r => setOverview(r.data))
            .catch(() => setOverview({}))
            .finally(() => setOverviewLoading(false));
    }, [teamId]);

    // Live match poller — always active so live games show on any tab
    useEffect(() => {
        const fetchLive = () => {
            axios.get(`${API_URL}/api/football/hub/team/${teamId}/live-now`, { headers: authHeaders() })
                .then(r => setLiveMatch(r.data.match || null))
                .catch(() => {});
        };
        fetchLive();  // check immediately on page load
        const timer = setInterval(fetchLive, 30000);
        return () => clearInterval(timer);
    }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Lazy-load fixtures, results, and squad when tab changes
    useEffect(() => {
        if (tab === 'fixtures' && !fixtures && !fixturesLoading) {
            setFixturesLoading(true);
            axios.get(`${API_URL}/api/football/hub/team/${teamId}/fixtures`, { headers: authHeaders() })
                .then(r => setFixtures(r.data.fixtures || []))
                .catch(() => setFixtures([]))
                .finally(() => setFixturesLoading(false));
        }
        if (tab === 'fixtures' && !results && !resultsLoading) {
            setResultsLoading(true);
            axios.get(`${API_URL}/api/football/hub/team/${teamId}/results`, { headers: authHeaders() })
                .then(r => setResults(r.data.results || []))
                .catch(() => setResults([]))
                .finally(() => setResultsLoading(false));
        }
        if (tab === 'squad' && !squad && !squadLoading) {
            setSquadLoading(true);
            axios.get(`${API_URL}/api/football/hub/team/${teamId}/squad`, { headers: authHeaders() })
                .then(r => setSquad(r.data.squad || {}))
                .catch(() => setSquad({}))
                .finally(() => setSquadLoading(false));
        }
    }, [tab, teamId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="container-app py-4 space-y-4">
            <div className="flex items-center justify-between">
                <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2 -ml-2 text-muted-foreground hover:text-foreground"
                    onClick={() => navigate(-1)}
                >
                    <ArrowLeft className="h-4 w-4" /> Back
                </Button>
                <Button
                    variant={following ? 'default' : 'outline'}
                    size="sm"
                    className={`gap-1.5 transition-all ${following ? 'bg-rose-500 hover:bg-rose-600 border-rose-500 text-white' : ''}`}
                    onClick={handleFollowToggle}
                    disabled={followLoading || overviewLoading}
                >
                    <Heart className={`h-3.5 w-3.5 ${following ? 'fill-current' : ''}`} />
                    {following ? 'Following' : 'Follow'}
                </Button>
            </div>

            <TeamHeader overview={overview} loading={overviewLoading} teamId={teamId} />

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="w-full grid grid-cols-3 h-11">
                    <TabsTrigger value="overview" className="text-sm font-semibold">Overview</TabsTrigger>
                    <TabsTrigger value="fixtures" className="text-sm font-semibold">Fixtures & Results</TabsTrigger>
                    <TabsTrigger value="squad" className="text-sm font-semibold">Squad</TabsTrigger>
                </TabsList>
                <TabsContent value="overview" className="mt-4">
                    {/* Show live match banner on overview so it's visible on page load */}
                    {liveMatch && (
                        <div className="mb-4">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse inline-block" />
                                <h3 className="text-sm font-semibold text-green-500 uppercase tracking-wide">Live Now</h3>
                            </div>
                            <MatchRow match={liveMatch} teamId={teamId} navigate={navigate} isResult={false} />
                        </div>
                    )}
                    <OverviewTab overview={overview} loading={overviewLoading} />
                </TabsContent>
                <TabsContent value="fixtures" className="mt-4">
                    <FixturesResultsTab
                        fixtures={fixtures}
                        fixturesLoading={fixturesLoading}
                        results={results}
                        resultsLoading={resultsLoading}
                        liveMatch={liveMatch}
                        teamId={teamId}
                        navigate={navigate}
                    />
                </TabsContent>
                <TabsContent value="squad" className="mt-4">
                    <SquadTab squad={squad} loading={squadLoading} onPlayerClick={setProfilePlayer} teamId={teamId} />
                </TabsContent>
            </Tabs>

            {profilePlayer && (
                <PlayerProfileModal
                    playerId={profilePlayer.player_id}
                    playerName={profilePlayer.player}
                    teamId={teamId}
                    compId={overview?.primary_comp_id}
                    layout="card"
                    onClose={() => setProfilePlayer(null)}
                />
            )}
        </div>
    );
}
